// /clientes/:id/extrato — historico financeiro do cliente em ordem cronologica.
// Mostra vendas a vista + CRs pagas + creditos da loja + devolucoes. Filtros por
// periodo (dias) e por tipo. Total no header soma vendas_vista + cr_paga (o que
// o cliente efetivamente pagou).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ReceiptText, CreditCard, Gift, Undo2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';

interface ClienteResumo {
  id: number;
  codigo_legado: string | null;
  nome: string;
  razao_social: string | null;
  cpf_cnpj: string | null;
  whatsapp: string | null;
  cidade: string | null;
  uf: string | null;
}

interface ExtratoLinha {
  data: string;
  tipo: 'venda_vista' | 'cr_paga' | 'credito_uso' | 'credito_estorno' | 'credito_cancelamento' | 'devolucao' | string;
  documento: string | null;
  venda_id: number | null;
  orcamento_numero: string | null;
  vendedor: string | null;
  forma_pagamento: string | null;
  valor: string | number;
  situacao: string | null;
}

interface ExtratoResp {
  cliente_id: number;
  periodo_dias: number;
  total_pago: number;
  linhas: ExtratoLinha[];
}

const TIPO_LABEL: Record<string, { label: string; cor: string; fundo: string; icone: typeof ReceiptText }> = {
  venda_vista:          { label: 'Venda a vista',  cor: '#0f766e', fundo: 'rgba(15,118,110,0.10)',  icone: ReceiptText },
  cr_paga:              { label: 'CR paga',         cor: '#2563eb', fundo: 'rgba(37,99,235,0.10)',   icone: CreditCard },
  credito_uso:          { label: 'Credito usado',   cor: '#7c3aed', fundo: 'rgba(124,58,237,0.10)',  icone: Gift },
  credito_estorno:      { label: 'Credito estorno', cor: '#7c3aed', fundo: 'rgba(124,58,237,0.10)',  icone: Gift },
  credito_cancelamento: { label: 'Credito cancel.', cor: '#9ca3af', fundo: 'rgba(156,163,175,0.10)', icone: Gift },
  devolucao:            { label: 'Devolucao',       cor: '#b91c1c', fundo: 'rgba(185,28,28,0.10)',   icone: Undo2 },
};

// Pode ser usada como rota (/clientes/:id/extrato) OU embutida em outra tela
// (ex: aba Extrato do Contas a Receber) passando `clienteId` direto. Quando
// embutida, esconde a seta de voltar e o wrapper de pagina.
export function ExtratoCliente({ clienteId: clienteIdProp, embutido = false }: { clienteId?: number; embutido?: boolean } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const clienteId = clienteIdProp ?? Number(id);

  const [cliente, setCliente] = useState<ClienteResumo | null>(null);
  const [extrato, setExtrato] = useState<ExtratoResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [dias, setDias] = useState<number>(365);
  const [filtroTipo, setFiltroTipo] = useState<string>('');

  useEffect(() => {
    if (!clienteId) return;
    let cancelado = false;
    setLoading(true);
    Promise.all([
      api.get<ClienteResumo>(`/api/clientes/${clienteId}`),
      api.get<ExtratoResp>(`/api/clientes/${clienteId}/extrato`, { query: { dias } }),
    ])
      .then(([c, e]) => { if (!cancelado) { setCliente(c); setExtrato(e); } })
      .catch((err) => { if (!cancelado) toast.erro((err as Error).message); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [clienteId, dias, toast]);

  const linhasFiltradas = useMemo(() => {
    if (!extrato) return [];
    if (!filtroTipo) return extrato.linhas;
    return extrato.linhas.filter((l) => l.tipo === filtroTipo);
  }, [extrato, filtroTipo]);

  const formas = useMemo(() => {
    if (!extrato) return [] as string[];
    const set = new Set<string>();
    extrato.linhas.forEach((l) => { if (l.forma_pagamento) set.add(l.forma_pagamento); });
    return Array.from(set).sort();
  }, [extrato]);

  const totalPorTipo = useMemo(() => {
    if (!extrato) return {} as Record<string, { qtd: number; total: number }>;
    const acc: Record<string, { qtd: number; total: number }> = {};
    for (const l of extrato.linhas) {
      const t = String(l.tipo);
      if (!acc[t]) acc[t] = { qtd: 0, total: 0 };
      acc[t].qtd += 1;
      acc[t].total += Number(l.valor || 0);
    }
    return acc;
  }, [extrato]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
        <Loader2 size={20} className="animate-spin" style={{ display: 'inline-block' }} />
      </div>
    );
  }

  if (!cliente) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Cliente nao encontrado</div>;
  }

  return (
    <div className={embutido ? '' : 'pagina'}>
      <div className="pagina-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {!embutido && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            title="Voltar"
            className="btn-icon"
            style={{ padding: 6 }}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <div style={{ flex: 1 }}>
          <h2 className="pagina-titulo" style={{ margin: 0 }}>
            Extrato — {cliente.nome}
            {cliente.codigo_legado && (
              <span className="text-gray-500 font-bold no-underline" style={{ fontSize: 14, marginLeft: 12 }}>
                {cliente.codigo_legado}
              </span>
            )}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {cliente.razao_social && <span>{cliente.razao_social} · </span>}
            {cliente.cpf_cnpj && <span>{cliente.cpf_cnpj} · </span>}
            {cliente.cidade && cliente.uf && <span>{cliente.cidade}/{cliente.uf}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL PAGO (janela)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f766e' }}>
            {formatMoeda(extrato?.total_pago ?? 0)}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8,
        padding: 12, marginBottom: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12,
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
            Periodo
          </label>
          <select
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
            style={{ padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13 }}
          >
            <option value={30}>Ultimos 30 dias</option>
            <option value={90}>Ultimos 90 dias</option>
            <option value={180}>Ultimos 6 meses</option>
            <option value={365}>Ultimos 12 meses</option>
            <option value={730}>Ultimos 24 meses</option>
            <option value={3650}>Todo o historico</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
            Tipo
          </label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13 }}
          >
            <option value="">Todos</option>
            <option value="venda_vista">Venda a vista</option>
            <option value="cr_paga">CR paga</option>
            <option value="credito_uso">Credito usado</option>
            <option value="credito_estorno">Credito estorno</option>
            <option value="credito_cancelamento">Credito cancelamento</option>
            <option value="devolucao">Devolucao</option>
          </select>
        </div>
        <div style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
          {Object.entries(totalPorTipo).map(([tipo, info]) => {
            const meta = TIPO_LABEL[tipo];
            return (
              <span key={tipo} style={{ marginLeft: 16, display: 'inline-block' }}>
                <span style={{ color: meta?.cor ?? 'inherit', fontWeight: 600 }}>
                  {meta?.label ?? tipo}
                </span>: {info.qtd} · {formatMoeda(info.total)}
              </span>
            );
          })}
        </div>
      </div>

      {/* Tabela */}
      {linhasFiltradas.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          Sem movimentacoes no periodo {filtroTipo ? `(tipo "${TIPO_LABEL[filtroTipo]?.label ?? filtroTipo}")` : ''}.
        </div>
      ) : (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--table-header-bg, #f3f4f6)', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Data</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Tipo</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Documento</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Vendedor</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Forma de pagamento</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Valor</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Situacao</th>
              </tr>
            </thead>
            <tbody>
              {linhasFiltradas.map((l, idx) => {
                const meta = TIPO_LABEL[String(l.tipo)] ?? { label: l.tipo, cor: 'inherit', fundo: 'transparent', icone: ReceiptText };
                const Icon = meta.icone;
                const docCompleto = [l.documento, l.orcamento_numero && l.orcamento_numero !== l.documento ? `(${l.orcamento_numero})` : null]
                  .filter(Boolean).join(' ');
                return (
                  <tr key={`${l.tipo}-${l.documento ?? idx}-${idx}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {formatDataHoraSP(l.data)}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{
                        background: meta.fundo, color: meta.cor,
                        padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        <Icon size={11} /> {meta.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{docCompleto || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{l.vendedor || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{l.forma_pagamento || '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                      {formatMoeda(l.valor)}
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
                      {l.situacao || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {formas.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
          Formas no periodo: {formas.join(' · ')}
        </div>
      )}
    </div>
  );
}
